import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useI18n } from '../utils/i18n';
import { Search, Ticket as TicketIcon, MessageCircle, Mail, History, Plus, Save, Paperclip, Trash2 } from 'lucide-react';
import apiService from '../services/apiService';
import { getFeatures } from '../utils/featuresCache';
import { useToast } from './ToastProvider';
import { useConfirm } from './ui/ConfirmDialog';
import Modal from './ui/Modal';
import Combobox from './ui/Combobox';
import MarkdownText from './ui/MarkdownText';
import '../styles/TicketView.css';
import '../styles/AiSearch.css';

// Highlight-Helfer für linke Karten
const escapeHtml = (str) => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const escapeRegex = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildTerms = (query) => {
  if (!query) return [];
  const terms = String(query).trim().split(/\s+/).filter(w => w.length >= 2);
  return Array.from(new Set(terms.map(t => t.toLowerCase())));
};

// Relevanzbewertung: nur Wörter mit Länge >= 4
const buildRelevanceTerms = (query) => {
  if (!query) return [];
  const terms = String(query).trim().split(/\s+/).filter(w => w.length >= 4);
  return Array.from(new Set(terms.map(t => t.toLowerCase())));
};

const highlightHtml = (text, query) => {
  try {
    if (!text) return '';
    const safe = escapeHtml(text);
    const terms = buildTerms(query);
    if (terms.length === 0) return safe;
    const pattern = new RegExp(`(${terms.map(escapeRegex).join('|')})`, 'gi');
    return safe.replace(pattern, '<mark style="background:var(--color-search-mark)">$1</mark>');
  } catch (_) {
    return escapeHtml(text);
  }
};

// Relevanz-Scoring-Helfer
const countMatches = (text, terms) => {
  try {
    if (!text || terms.length === 0) return 0;
    const pattern = new RegExp(`(${terms.map(escapeRegex).join('|')})`, 'gi');
    const m = String(text).match(pattern);
    return m ? m.length : 0;
  } catch (_) { return 0; }
};

const containsPhrase = (text, phrase) => {
  try {
    const p = String(phrase || '').trim();
    if (!text || !p || p.length < 3) return false;
    return String(text).toLowerCase().includes(p.toLowerCase());
  } catch (_) { return false; }
};

const containsAnyPair = (text, pairs) => {
  try {
    if (!text || !Array.isArray(pairs) || pairs.length === 0) return false;
    const lower = String(text).toLowerCase();
    return pairs.some(p => lower.includes(String(p || '').toLowerCase()));
  } catch (_) { return false; }
};

const priorityBoost = (priority) => {
  switch (String(priority || '').toLowerCase()) {
    case 'kritisch': return 2;
    case 'hoch': return 1;
    default: return 0;
  }
};

const statusBoost = (status) => {
  switch (String(status || '').toLowerCase()) {
    case 'offen': return 1;
    case 'in_bearbeitung': return 0.5;
    default: return 0;
  }
};

const ticketNumberBoost = (query, ticketNumber) => {
  try {
    const q = String(query || '').toLowerCase();
    const num = String(ticketNumber || '').toLowerCase();
    if (!num) return 0;
    if (q.includes(`#${num}`)) return 6;
    // Nummer pur im Query (z. B. "1234")
    if (q.split(/\s+/).some(t => t === num)) return 6;
    return 0;
  } catch (_) { return 0; }
};

const AnimatedDots = () => (
  <div className="ai-chat-dots" aria-hidden="true">
    <span className="ai-chat-dot" />
    <span className="ai-chat-dot" />
    <span className="ai-chat-dot" />
  </div>
);

// #122: Chat-Interface für die KI-Suche. Erste Nachricht löst die volle
// Suche (Optimizer/Retrieval + Antwort) aus, weitere Nachrichten laufen als
// Rückfragen gegen das gespeicherte Kontextfenster. Verläufe lassen sich im
// eigenen Benutzerkontext speichern und als interner Kommentar an ein Ticket
// anhängen.
const GlobalSearchView = () => {
  const { t, getLocale } = useI18n();
  const { pushToast } = useToast();
  const confirm = useConfirm();
  const [composer, setComposer] = useState('');
  const [messages, setMessages] = useState([]); // [{role:'user'|'assistant', content, ts}]
  const [contextText, setContextText] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [followupLoading, setFollowupLoading] = useState(false);
  const [ticketResults, setTicketResults] = useState([]);
  const [cards, setCards] = useState([]);
  const [aiDebug, setAiDebug] = useState(null);
  const [error, setError] = useState(null);
  const [leftItems, setLeftItems] = useState([]);
  const [leftLoading, setLeftLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [submittedQuery, setSubmittedQuery] = useState('');
  // Verläufe (#122)
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // An Ticket anhängen (#122)
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachTicketId, setAttachTicketId] = useState(null);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [attaching, setAttaching] = useState(false);
  const threadEndRef = useRef(null);
  // F2: KI-Suche kann im Admin-ai-Tab deaktiviert sein
  const [searchAiEnabled, setSearchAiEnabled] = useState(true);
  useEffect(() => {
    let cancelled = false;
    getFeatures().then(features => {
      if (!cancelled) setSearchAiEnabled(features.searchAi !== false);
    });
    return () => { cancelled = true; };
  }, []);

  // Neue Nachrichten in Sicht halten
  useEffect(() => {
    try { threadEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch (_) {}
  }, [messages, loading, followupLoading]);

  // Erste Nachricht: volle Suche (Optimizer/Retrieval + finale Antwort)
  const runSearch = useCallback(async (q) => {
    if (!q || q.length < 3) return;
    setSubmittedQuery(q);
    setHasSearched(true);
    setLoading(true);
    setError(null);
    setConversationId(null);
    setContextText('');
    setMessages([{ role: 'user', content: q, ts: Date.now() }]);
    setComposer('');
    try {
      const res = await apiService.globalSearch(q);
      const results = res?.results || {};
      setTicketResults(results.tickets || []);
      setCards(Array.isArray(results.cards) ? results.cards : []);
      setAiDebug(res?.ai?.debug || null);
      const answer = res?.ai?.answer || '';
      // Kontextfenster für Rückfragen: seit #122 explizit im Payload,
      // Regex auf den userPrompt bleibt als Fallback für gecachte Antworten
      const ctx = res?.ai?.contextText
        || (res?.ai?.debug?.userPrompt ? String(res.ai.debug.userPrompt).replace(/^Context:\n([\s\S]*?)\n\nQuestion:[\s\S]*$/m, '$1') : '');
      setContextText(ctx || '');
      setMessages(prev => [...prev, { role: 'assistant', content: answer || t('search.noAnswer', 'Keine Antwort erhalten.'), ts: Date.now() }]);
    } catch (e) {
      if (e?.response?.data?.aiDisabled) {
        setSearchAiEnabled(false);
        setError(null);
        setMessages([]);
      } else {
        setError(e?.message || 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Folgenachrichten: Rückfrage gegen das bestehende Kontextfenster
  const sendFollowup = useCallback(async (q) => {
    if (!contextText) {
      pushToast({ message: t('search.noContext', 'Für diesen Verlauf sind keine Rückfragen möglich.'), type: 'info' });
      return;
    }
    setFollowupLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: q, ts: Date.now() }]);
    setComposer('');
    try {
      const firstUser = messages.find(m => m.role === 'user');
      const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant');
      const res = await apiService.aiFollowup({
        followup: q,
        originalQuestion: firstUser?.content || '',
        contextText,
        previousAnswer: lastAssistant?.content || ''
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res?.answer || t('search.noAnswer', 'Keine Antwort erhalten.'), ts: Date.now() }]);
    } catch (e) {
      if (e?.response?.data?.aiDisabled) {
        setSearchAiEnabled(false);
      } else {
        pushToast({ message: t('search.followupFailed', 'Rückfrage fehlgeschlagen.'), type: 'error' });
      }
    } finally {
      setFollowupLoading(false);
    }
  }, [contextText, messages, pushToast, t]);

  const onComposerSubmit = useCallback((e) => {
    if (e && e.preventDefault) e.preventDefault();
    const q = composer.trim();
    if (!q || loading || followupLoading) return;
    if (messages.length === 0) runSearch(q);
    else sendFollowup(q);
  }, [composer, loading, followupLoading, messages.length, runSearch, sendFollowup]);

  const startNewSearch = useCallback(() => {
    setMessages([]);
    setContextText('');
    setConversationId(null);
    setComposer('');
    setTicketResults([]);
    setCards([]);
    setAiDebug(null);
    setLeftItems([]);
    setLeftLoading(false);
    setError(null);
    setHasSearched(false);
    setSubmittedQuery('');
  }, []);

  // Verläufe laden/öffnen/löschen (#122)
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiService.listSearchConversations();
      setHistoryItems(Array.isArray(res?.conversations) ? res.conversations : []);
    } catch (_) {
      setHistoryItems([]);
    }
    setHistoryLoading(false);
  }, []);

  const toggleHistory = useCallback(() => {
    setShowHistory(prev => {
      const next = !prev;
      if (next) loadHistory();
      return next;
    });
  }, [loadHistory]);

  const openConversation = useCallback(async (id) => {
    try {
      const res = await apiService.getSearchConversation(id);
      const conv = res?.conversation;
      if (!conv) return;
      setMessages(Array.isArray(conv.messages) ? conv.messages : []);
      setContextText(conv.contextText || '');
      setConversationId(conv.id);
      setShowHistory(false);
      // Trefferkarten gehören zur Live-Suche, nicht zum gespeicherten Verlauf
      setTicketResults([]);
      setCards([]);
      setAiDebug(null);
      setLeftItems([]);
      setHasSearched(false);
      setSubmittedQuery('');
      setError(null);
    } catch (_) {
      pushToast({ message: t('search.loadFailed', 'Verlauf konnte nicht geladen werden.'), type: 'error' });
    }
  }, [pushToast, t]);

  const deleteConversation = useCallback(async (id) => {
    const ok = await confirm({
      message: t('search.historyDeleteConfirm', 'Diesen Verlauf löschen?'),
      danger: true
    });
    if (!ok) return;
    try {
      await apiService.deleteSearchConversation(id);
      setHistoryItems(prev => prev.filter(c => c.id !== id));
      if (conversationId === id) setConversationId(null);
    } catch (_) {
      pushToast({ message: t('search.deleteFailed', 'Löschen fehlgeschlagen.'), type: 'error' });
    }
  }, [confirm, conversationId, pushToast, t]);

  const saveConversation = useCallback(async () => {
    if (saving || messages.length === 0) return;
    setSaving(true);
    try {
      if (conversationId) {
        await apiService.updateSearchConversation(conversationId, { messages });
      } else {
        const res = await apiService.createSearchConversation({ messages, contextText });
        if (res?.conversation?.id) setConversationId(res.conversation.id);
      }
      pushToast({ message: t('search.savedToast', 'Verlauf gespeichert.'), type: 'success' });
      if (showHistory) loadHistory();
    } catch (_) {
      pushToast({ message: t('search.saveFailed', 'Speichern fehlgeschlagen.'), type: 'error' });
    }
    setSaving(false);
  }, [saving, messages, conversationId, contextText, pushToast, t, showHistory, loadHistory]);

  // An Ticket anhängen: Verlauf als interner Kommentar (#122)
  const openAttachModal = useCallback(async () => {
    setShowAttachModal(true);
    setAttachTicketId(null);
    if (ticketOptions.length === 0) {
      try {
        const res = await apiService.getTickets();
        const list = res?.tickets || [];
        setTicketOptions(list.map(tk => ({ value: tk.id, label: `#${tk.ticketNumber || tk.id} · ${tk.title || ''}` })));
      } catch (_) {}
    }
  }, [ticketOptions.length]);

  const attachToTicket = useCallback(async () => {
    if (!attachTicketId || attaching) return;
    setAttaching(true);
    try {
      const lines = [t('search.attachCommentTitle', 'KI-Suche – Verlauf')];
      for (const m of messages) {
        lines.push('', `${m.role === 'user' ? t('search.roleQuestion', 'Frage') : t('search.roleAnswer', 'Antwort')}: ${m.content}`);
      }
      await apiService.addCommentToTicket(attachTicketId, lines.join('\n'), true);
      pushToast({ message: t('search.attachedToast', 'Als interner Kommentar am Ticket gespeichert.'), type: 'success' });
      setShowAttachModal(false);
    } catch (_) {
      pushToast({ message: t('search.attachFailed', 'Anhängen fehlgeschlagen (fehlende Berechtigung?).'), type: 'error' });
    }
    setAttaching(false);
  }, [attachTicketId, attaching, messages, pushToast, t]);

  // Linke Kartenliste: kombiniert Basis-Ergebnisse + Optimizer-Suchpaare, sortiert nach Relevanz
  useEffect(() => {
    let cancelled = false;
    const build = async () => {
      try {
        setLeftLoading(true);
        const items = [];
        const seen = new Set();
        const terms = buildRelevanceTerms(submittedQuery);
        const pairs = Array.isArray(aiDebug?.usedQueries) ? aiDebug.usedQueries : [];
        const addTicket = (b) => {
          if (!b || b.ticketId == null) return;
          const key = `ticket:${b.ticketId}`;
          const hits = Array.isArray(b.hits) ? b.hits : [];
          const hitsMax = Math.max(...hits.map(h => h.sortTimestamp || 0), 0);
          const tsTicket = Math.max(b.latestTimestamp || 0, hitsMax);
          if (!seen.has(key)) {
            seen.add(key);
            // Relevanz-Score für Ticket (Titel + Nummer + Meta)
            const title = b.title || '';
            const scoreTitle = 5 * countMatches(title, terms);
            const phrase = submittedQuery && submittedQuery.length >= 3 && containsPhrase(title, submittedQuery) ? 8 : 0;
            const prio = priorityBoost(b.priority);
            const stat = statusBoost(b.status);
            const numBoost = ticketNumberBoost(submittedQuery, b.ticketNumber);
            const pairBoost = containsAnyPair(title, pairs) ? 4 : 0;
            const score = scoreTitle + phrase + (2 * prio) + stat + numBoost + pairBoost;
            items.push({ kind: 'ticket', ts: tsTicket, bucket: b, score });
          }
          for (const h of hits) addHit(b, h);
        };
        const addHit = (b, h) => {
          if (!b) return;
          const key = `hit:${b.ticketId}:${h?.type || ''}:${h?.commentId || ''}:${h?.emailId || ''}:${h?.sortTimestamp || 0}:${(h?.subject || '').slice(0,64)}`;
          if (seen.has(key)) return;
          seen.add(key);
          // Relevanz-Score für Hit (Subject + Snippet)
          const subject = h?.subject || h?.type || '';
          const snippet = h?.snippet || '';
          const scoreSubject = 3 * countMatches(subject, terms);
          const scoreSnippet = 1 * countMatches(snippet, terms);
          const phraseSubj = submittedQuery && submittedQuery.length >= 3 && containsPhrase(subject, submittedQuery) ? 6 : 0;
          const phraseSnip = submittedQuery && submittedQuery.length >= 3 && containsPhrase(snippet, submittedQuery) ? 3 : 0;
          const pairBoost = containsAnyPair(`${subject}\n${snippet}`, pairs) ? 4 : 0;
          const numBoost = ticketNumberBoost(submittedQuery, b.ticketNumber);
          const score = scoreSubject + scoreSnippet + phraseSubj + phraseSnip + pairBoost + numBoost;
          items.push({ kind: 'hit', ts: h?.sortTimestamp || 0, bucket: b, hit: h || {}, score });
        };

        // Basis: aus der Hauptsuche (nur vorhandene Buckets nutzen)
        for (const b of Array.from(ticketResults || [])) addTicket(b);
        // Karten aus Backend (direkt aus KI-Suche erzeugt)
        if (Array.isArray(cards) && cards.length > 0) {
          for (const b of cards) {
            addTicket({
              ticketId: b.ticketId,
              ticketNumber: b.ticketNumber,
              title: b.title,
              latestTimestamp: b.latestTimestamp,
              hits: Array.isArray(b.hits) ? b.hits : []
            });
          }
        }

        // Sortierung: Relevanz first, Voll-Tickets ans Ende
        items.sort((a, b) => {
          const aFull = !!(a?.bucket?.isFullTicketSelected);
          const bFull = !!(b?.bucket?.isFullTicketSelected);
          if (aFull !== bFull) return aFull ? 1 : -1; // Voll-Tickets nach unten
          const sa = typeof a.score === 'number' ? a.score : 0;
          const sb = typeof b.score === 'number' ? b.score : 0;
          if (sb !== sa) return sb - sa;
          return (b.ts || 0) - (a.ts || 0);
        });
        if (!cancelled) setLeftItems(items);
      } finally {
        if (!cancelled) setLeftLoading(false);
      }
    };
    build();
    return () => { cancelled = true; };
  }, [submittedQuery, aiDebug, ticketResults, cards]);

  const hasAssistantMessage = messages.some(m => m.role === 'assistant');
  const showCardsColumn = hasSearched || leftLoading || leftItems.length > 0;

  return (
    <div className="container">
      <div className="ticket-view">
        <div className="ticket-header ai-search-header">
          <h2>
            <Search size={24} style={{ marginRight: '10px', verticalAlign: 'middle' }} />
            {t('search.title', 'KI-Suche')}
          </h2>
          {searchAiEnabled && (
            <div className="ai-search-header-actions">
              {messages.length > 0 && (
                <button type="button" className="btn btn-secondary btn-sm" onClick={startNewSearch}>
                  <Plus size={14} /> {t('search.newSearch', 'Neue Suche')}
                </button>
              )}
              <button type="button" className="btn btn-secondary btn-sm" onClick={toggleHistory}>
                <History size={14} /> {t('search.history', 'Verläufe')}
              </button>
            </div>
          )}
        </div>

        {!searchAiEnabled && (
          <div className="ticket-empty-state" style={{ maxWidth: 1000, margin: '12px auto' }}>
            <Search size={32} style={{ opacity: 0.3 }} />
            <p>{t('search.aiDisabled', 'Die KI-Suche ist deaktiviert. Ein Administrator kann sie im Admin-Panel (Tab „AI Integration") aktivieren.')}</p>
          </div>
        )}

        {searchAiEnabled && showHistory && (
          <div className="ai-search-history">
            {historyLoading && (
              <div className="ai-search-history-empty"><AnimatedDots /></div>
            )}
            {!historyLoading && historyItems.length === 0 && (
              <div className="ai-search-history-empty">{t('search.historyEmpty', 'Noch keine gespeicherten Verläufe.')}</div>
            )}
            {!historyLoading && historyItems.map(c => (
              <div key={c.id} className="ai-search-history-row" onClick={() => openConversation(c.id)}>
                <div className="ai-search-history-main">
                  <div className="ai-search-history-title">{c.title}</div>
                  <div className="ai-search-history-meta">
                    {new Date(c.updatedAt).toLocaleString(getLocale(), { dateStyle: 'short', timeStyle: 'short' })}
                    {' · '}
                    {t('search.historyMessages', '{{count}} Nachrichten', { count: c.messageCount })}
                  </div>
                </div>
                <button
                  type="button"
                  className="ai-search-history-delete"
                  title={t('search.historyDelete', 'Verlauf löschen')} aria-label={t('search.historyDelete', 'Verlauf löschen')}
                  onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {searchAiEnabled && (
          <div className="ai-search-layout">
            {/* Linke Spalte: Trefferkarten der Live-Suche */}
            {showCardsColumn && (
              <div className="ai-search-cards">
                <h4 className="ai-search-cards-title">{t('search.hits', 'Treffer')}</h4>
                {leftItems.length === 0 && !loading && !leftLoading && hasSearched && (
                  <div className="ticket-empty-state">
                    <TicketIcon size={32} style={{ opacity: 0.3 }} />
                    <p>{t('search.noResults', 'Keine Ergebnisse')}</p>
                  </div>
                )}
                {leftLoading && <AnimatedDots />}
                {leftItems.map((item, idx) => {
                  if (item.kind === 'ticket') {
                    const b = item.bucket;
                    return (
                      <div
                        key={`ticket-${b.ticketId}-${idx}`}
                        className="dashboard-ticket-item"
                        onClick={() => { try { window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId: b.ticketId } })); } catch (_) {} }}
                        style={{ cursor: 'pointer', padding: 12, border: '1px solid var(--color-border-card)', borderRadius: 6, background: 'var(--color-surface)' }}
                      >
                        <div className="dashboard-ticket-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TicketIcon size={14} />
                          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }} dangerouslySetInnerHTML={{ __html: highlightHtml(b.title || '', submittedQuery) }} />
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>#{b.ticketNumber}</div>
                      </div>
                    );
                  }
                  // hit card
                  const b = item.bucket;
                  const h = item.hit || {};
                  const icon = h.type === 'comment' ? <MessageCircle size={14} /> : (h.type === 'email' ? <Mail size={14} /> : <TicketIcon size={14} />);
                  return (
                    <div
                      key={`hit-${b.ticketId}-${idx}`}
                      className="ticket-column-item"
                      onClick={() => { try { const scrollTo = { type: h.type === 'comment' ? 'comment' : (h.type === 'email' ? 'email' : h.type), commentId: h.commentId || null, emailId: h.emailId || null, subject: h.subject || null, snippet: h.snippet || null }; window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId: b.ticketId, scrollTo } })); } catch (_) {} }}
                      style={{ cursor: 'pointer', padding: 10, border: '1px solid var(--color-border-card)', borderRadius: 6, background: 'var(--color-surface)' }}
                    >
                      <div className="ticket-column-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {icon}
                        <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis' }} dangerouslySetInnerHTML={{ __html: highlightHtml(h.subject || h.type || '', submittedQuery) }} />
                        {/* #14: Herkunfts-Badge für Treffer aus dem Embedding-Index */}
                        {h.origin === 'semantic' && (
                          <span title={typeof h.score === 'number' ? `Score ${h.score}` : undefined} style={{ fontSize: 'var(--text-micro)', fontWeight: 600, color: 'var(--color-accent-2)', border: '1px solid var(--color-accent-2)', borderRadius: 8, padding: '0 6px', whiteSpace: 'nowrap' }}>
                            {t('search.semanticBadge', 'semantisch')}
                          </span>
                        )}
                      </div>
                      {h.snippet && (
                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12, color: 'var(--color-text-muted)', margin: 0, marginTop: 6 }} dangerouslySetInnerHTML={{ __html: highlightHtml(h.snippet || '', submittedQuery) }} />
                      )}
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>#{b.ticketNumber}</div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Chat-Bereich */}
            <div className="ai-search-chat" style={showCardsColumn ? undefined : { maxWidth: 1000, margin: '0 auto', width: '100%' }}>
              <div className="ai-chat-thread">
                {messages.length === 0 && !loading && (
                  <div className="ai-chat-empty">
                    {t('search.emptyHint', 'Stelle eine Frage — die KI durchsucht Tickets, Kommentare und E-Mails.')}
                  </div>
                )}
                {messages.map((m, idx) => (
                  <div key={idx} className={`ai-chat-msg ${m.role === 'user' ? 'user' : 'assistant'}`}>
                    {/* #231a: KI-Antworten kommen als Markdown — rendern statt roh anzeigen */}
                    <MarkdownText>{m.content}</MarkdownText>
                  </div>
                ))}
                {(loading || followupLoading) && <AnimatedDots />}
                {error && <div className="error-message">{error}</div>}
                <div ref={threadEndRef} />
              </div>
              {hasAssistantMessage && !loading && (
                <div className="ai-chat-actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={saveConversation} disabled={saving}>
                    <Save size={14} /> {t('search.saveConversation', 'Verlauf speichern')}
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={openAttachModal}>
                    <Paperclip size={14} /> {t('search.attachToTicket', 'An Ticket anhängen')}
                  </button>
                </div>
              )}
              <form className="ai-chat-composer" onSubmit={onComposerSubmit}>
                <input
                  type="text"
                  value={composer}
                  onChange={(e) => setComposer(e.target.value)}
                  placeholder={messages.length === 0
                    ? t('search.placeholder', 'Was liegt dir auf dem Herzen?')
                    : t('search.ai.followup.placeholder', 'Rückfrage stellen…')}
                  disabled={loading || followupLoading}
                />
                <button type="submit" className="btn btn-primary" disabled={loading || followupLoading || !composer.trim()}>
                  {t('search.send', 'Senden')}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Debug-Log unterhalb des Chats (weiterhin sichtbar) */}
        {!loading && aiDebug && aiDebug?.debugEnabled !== false && (
          <div style={{ marginTop: 12, padding: '10px 12px', border: '1px dashed var(--color-border-input)', borderRadius: 6, background: 'var(--color-bg)' }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('search.ai.debug', 'Debug (nur sichtbar hier)')}</div>
            {/* Schritt-für-Schritt */}
            {Array.isArray(aiDebug.steps) && aiDebug.steps.map((s, idx) => (
              <div key={idx} style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 500 }}>{s.title || s.step}</div>
                {s.step === 'optimizer_start' && (
                  <>
                    <div><b>{t('search.ai.input', 'Suchbegriff')}:</b> {s.query}</div>
                    <div style={{ marginTop: 4 }}><b>Prompt:</b></div>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{s.prompt}</pre>
                  </>
                )}
                {s.step === 'optimizer_result' && (
                  <>
                    <div style={{ marginTop: 4 }}><b>{t('search.ai.usedQueries', 'Genutzte Suchpaare')}:</b></div>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{(s.pairs || []).join('\n')}</pre>
                  </>
                )}
                {s.step === 'optimizer_failed' && (
                  <div style={{ color: 'var(--color-error-text-strong)' }}>
                    <div>{s.message}</div>
                    {s.details && <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{s.details}</pre>}
                  </div>
                )}
                {s.step === 'optimizer_fallback' && (
                  <>
                    <div style={{ marginTop: 4 }}><b>{t('search.ai.usedQueries', 'Genutzte Suchpaare (Fallback)')}:</b></div>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{(s.pairs || []).join('\n')}</pre>
                  </>
                )}
                {s.step === 'db_search' && (
                  <>
                    {(s.perQuery || []).map((pq, i) => (
                      <div key={i} style={{ marginTop: 6 }}>
                        <div><b>Suchpaar:</b> {pq.query}</div>
                        {pq.counts && (
                          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>
                            {`Tickets: ${pq.counts.ticketsActive}/${pq.counts.ticketsArchived}, `}
                            {`Kommentare: ${pq.counts.commentsActive}/${pq.counts.commentsArchived} (vollständig), `}
                            {`E-Mails: ${pq.counts.emailsActive}/${pq.counts.emailsArchived}, `}
                            {`Fragmente: ${pq.counts.fragments}`}
                          </div>
                        )}
                        {pq.error && <div style={{ color: 'var(--color-error-text-strong)' }}>{pq.error}</div>}
                      </div>
                    ))}
                  </>
                )}
                {s.step === 'final_call' && (
                  <>
                    <div><b>Context-Zeichen:</b> {s.contextChars}</div>
                    <div><b>Fragmente gesamt:</b> {s.fragmentsTotal}</div>
                  </>
                )}
              </div>
            ))}

            {/* Kompakte Gesamtübersicht */}
            {Array.isArray(aiDebug.usedQueries) && aiDebug.usedQueries.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>{t('search.ai.usedQueries', 'Genutzte Suchpaare')}:</div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{aiDebug.usedQueries.join('\n')}</pre>
              </div>
            )}
            {aiDebug.systemPrompt && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>System-Prompt:</div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{aiDebug.systemPrompt}</pre>
              </div>
            )}
            {aiDebug.userPrompt && (
              <div>
                <div style={{ fontWeight: 500, marginBottom: 4 }}>User-Prompt (inkl. Kontext):</div>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{aiDebug.userPrompt}</pre>
              </div>
            )}
          </div>
        )}
      </div>

      {/* An Ticket anhängen (#122) */}
      <Modal
        open={showAttachModal}
        onClose={() => { if (!attaching) setShowAttachModal(false); }}
        title={t('search.attachToTicket', 'An Ticket anhängen')}
        icon={<Paperclip size={18} />}
        maxWidth="480px"
        closeDisabled={attaching}
        footer={(
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setShowAttachModal(false)} disabled={attaching}>
              {t('common.cancel', 'Abbrechen')}
            </button>
            <button type="button" className="btn btn-primary" onClick={attachToTicket} disabled={attaching || !attachTicketId}>
              {t('search.attachAction', 'Anhängen')}
            </button>
          </>
        )}
      >
        <p style={{ marginTop: 0 }}>{t('search.attachHint', 'Der Verlauf wird als interner Kommentar am gewählten Ticket gespeichert.')}</p>
        <Combobox
          options={ticketOptions}
          value={attachTicketId}
          onChange={setAttachTicketId}
          placeholder={t('search.attachSelectTicket', 'Ticket wählen')}
          noResultsText={t('search.noResults', 'Keine Ergebnisse')}
        />
      </Modal>
    </div>
  );
};

export default GlobalSearchView;
